import { GroupBox, InputsWrapper } from '../atoms';
import { useDragAndDropContext } from '@ardoq/drag-and-drop';
import { GhostButton, DragButton } from '@ardoq/button';
import { DeleteIcon } from '@ardoq/icons';
import { GroupType } from '@ardoq/api-types';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import {
  GetInvalidGroupingSelectOptions,
  GetRuleNotificationParams,
  PerspectiveGroupingRule,
  PerspectivesGroupsOptions,
} from './types';
import { RuleNotificationType } from '@ardoq/query-builder';
import { ReferenceTypeGroupRule } from './GroupingRules/ReferenceTypeGroupRule';
import { WorkspaceGroupRule } from './GroupingRules/WorkspaceGroupRule';
import { ComponentTypeGroupRule } from './GroupingRules/ComponentTypeGroupRule';
import { ParentAllGroupRule } from './GroupingRules/ParentAllGroupRule';
import { ChildGroupRule } from './GroupingRules/ChildGroupRule';
import { TagGroupRule } from './GroupingRules/TagGroupRule';
import { FieldGroupRule } from './GroupingRules/FieldGroupRule';
import { ParentGroupRule } from './GroupingRules/ParentGroupRule';
import { WarningNotificationRule } from '../WarningNotificationRule';
import { DisabledOptionProps } from '../types';
import { perspectivesRowDragAndDropHandlers } from '../perspectivesRowDragAndDropHandlers';
import { ReferenceTypeNameGroupRule } from './GroupingRules/ReferenceTypeNameGroupRule';
import { type I18nLocale, t } from '@ardoq/i18n';

type GroupProps = {
  dragAndDropCardId: string; // for drag&drop only
  groupingRule: PerspectiveGroupingRule;
  groupingOptions: PerspectivesGroupsOptions;
  getRuleNotificationParams?: GetRuleNotificationParams;
  onGroupingRuleDeleted: (groupingRuleId: string) => void;
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  disabledOptionProps?: DisabledOptionProps;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  isDisabled?: boolean;
  /**
   * Whether the REFERENCE_TYPE_NAME grouping rule type should render its
   * dedicated input. Wired from the consuming app's feature flag
   * (Features.GROUPING_BY_TYPE_NAMES) to keep this package free of the
   * deprecated @ardoq/features singleton.
   */
  isGroupingByTypeNamesEnabled?: boolean;
  i18nLocale: I18nLocale;
};

const Group = ({
  groupingRule,
  groupingOptions,
  onGroupingRuleDeleted,
  onGroupingRuleUpdated,
  getRuleNotificationParams,
  disabledOptionProps,
  getInvalidGroupingSelectOptions,
  isDisabled,
  isGroupingByTypeNamesEnabled = false,
  i18nLocale,
}: GroupProps) => {
  const { onDragStart, onDragOver, onDragEnd } =
    perspectivesRowDragAndDropHandlers(
      groupingRule.id || null,
      useDragAndDropContext()
    );

  const { message, notificationType } =
    getRuleNotificationParams?.(groupingRule) || {};
  const isWarning =
    notificationType === RuleNotificationType.WARNING &&
    !getInvalidGroupingSelectOptions?.(groupingRule);

  if (isWarning) {
    return (
      <WarningNotificationRule
        message={message}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        removeRule={() => onGroupingRuleDeleted(groupingRule.id)}
        dataTestId="group-box-grouping-warning-notification"
      />
    );
  }

  const isDraggable = !!groupingRule.id && !isDisabled;

  const fieldsTabIndex = isDisabled ? -1 : 0;

  const type = groupingRule.type;

  return (
    <GroupBox
      data-testid={`group-box-group-by-${type}`}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <DragButton
        draggable={isDraggable}
        isDisabled={isDisabled}
        data-testid="drag-button"
        i18nLocale={i18nLocale}
      />
      <InputsWrapper data-testid="inputs-wrapper" $isDisabled={isDisabled}>
        {isGroupingByTypeNamesEnabled &&
          type === GroupType.REFERENCE_TYPE_NAME && (
            <ReferenceTypeNameGroupRule
              onGroupingRuleUpdated={onGroupingRuleUpdated}
              groupingRule={groupingRule}
              referenceTypeNames={groupingOptions.globalReferenceTypeNames}
              tabIndex={fieldsTabIndex}
              disabledOptionProps={disabledOptionProps}
              getInvalidGroupingSelectOptions={getInvalidGroupingSelectOptions}
              i18nLocale={i18nLocale}
            />
          )}
        {type === GroupType.REFERENCE && (
          <ReferenceTypeGroupRule
            onGroupingRuleUpdated={onGroupingRuleUpdated}
            groupingRule={groupingRule}
            workspaces={groupingOptions.workspaces}
            referenceTypesPerWorkspace={
              groupingOptions.referenceTypesPerWorkspace
            }
            tabIndex={fieldsTabIndex}
            disabledOptionProps={disabledOptionProps}
            getInvalidGroupingSelectOptions={getInvalidGroupingSelectOptions}
            i18nLocale={i18nLocale}
          />
        )}
        {type === GroupType.WORKSPACE && (
          <WorkspaceGroupRule i18nLocale={i18nLocale} />
        )}
        {type === GroupType.COMPONENT && (
          <ComponentTypeGroupRule i18nLocale={i18nLocale} />
        )}
        {type === GroupType.PARENT && (
          <ParentGroupRule
            onGroupingRuleUpdated={onGroupingRuleUpdated}
            groupingRule={groupingRule}
            workspaces={groupingOptions.workspaces}
            componentTypesPerWorkspace={
              groupingOptions.componentTypesPerWorkspace
            }
            tabIndex={fieldsTabIndex}
            disabledOptionProps={disabledOptionProps}
            getInvalidGroupingSelectOptions={getInvalidGroupingSelectOptions}
            i18nLocale={i18nLocale}
          />
        )}
        {type === GroupType.PARENT_ALL && (
          <ParentAllGroupRule
            tabIndex={fieldsTabIndex}
            groupingRule={groupingRule}
            onGroupingRuleUpdated={onGroupingRuleUpdated}
            i18nLocale={i18nLocale}
          />
        )}
        {type === GroupType.CHILD && (
          // ChildGroupingRule has been deprecated. User cannot create a new grouping rule of this type.
          // User still can display such rule if it's part of a saved perspective, but it's read-only and cannot be updated.
          <ChildGroupRule
            groupingRule={groupingRule}
            workspaces={groupingOptions.workspaces}
            componentTypesPerWorkspace={
              groupingOptions.componentTypesPerWorkspace
            }
            i18nLocale={i18nLocale}
          />
        )}
        {type === GroupType.FIELD && (
          <FieldGroupRule
            onGroupingRuleUpdated={onGroupingRuleUpdated}
            groupingRule={groupingRule}
            workspaces={groupingOptions.workspaces}
            fieldsPerWorkspace={groupingOptions.fieldsPerWorkspace}
            tabIndex={fieldsTabIndex}
            disabledOptionProps={disabledOptionProps}
            getInvalidGroupingSelectOptions={getInvalidGroupingSelectOptions}
            i18nLocale={i18nLocale}
          />
        )}
        {type === GroupType.TAG && (
          <TagGroupRule
            onGroupingRuleUpdated={onGroupingRuleUpdated}
            groupingRule={groupingRule}
            workspaces={groupingOptions.workspaces}
            tagsPerWorkspace={groupingOptions.tagsPerWorkspace}
            tabIndex={fieldsTabIndex}
            disabledOptionProps={disabledOptionProps}
            getInvalidGroupingSelectOptions={getInvalidGroupingSelectOptions}
            i18nLocale={i18nLocale}
          />
        )}
      </InputsWrapper>
      <GhostButton
        dataTestId="delete-grouping-rule"
        onClick={() => onGroupingRuleDeleted(groupingRule.id)}
      >
        <VisuallyHidden>
          {t(
            i18nLocale,
            'perspectives:perspectiveGroup.deleteGrouping',
            'Delete grouping'
          )}
        </VisuallyHidden>
        <DeleteIcon />
      </GhostButton>
    </GroupBox>
  );
};

export { Group };
